// src/decodeEquipmentSerial.ts
import { toLocalDate, unitBrandName } from "@rock/util";

// src/decodeSerial.ts
import { foldNameplateGlyphs, normalizeNameKey } from "@rock/util";

// src/format.ts
var MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var MONTH_ABBREVIATIONS = MONTH_NAMES.map((name) => name.slice(0, 3));
function formatMonthYear(year, month) {
  return month ? `${MONTH_ABBREVIATIONS[month - 1]} ${year}` : `${year}`;
}
function joinOr(items) {
  if (items.length <= 1) return `${items[0] ?? ""}`;
  return `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
}

// src/decodeHelpers.ts
function cycleYears(anchor, cycle, ctx, era) {
  const from = Math.max(ctx.minYear, era?.from ?? ctx.minYear);
  const to = Math.min(ctx.nowYear, era?.to ?? ctx.nowYear);
  const years = [];
  for (let year = from; year <= to; year++) {
    if (positiveMod(year - anchor, cycle) === 0) years.push(year);
  }
  return years;
}
function yearsInWindow(years, ctx, era) {
  const from = Math.max(ctx.minYear, era?.from ?? ctx.minYear);
  const to = Math.min(ctx.nowYear, era?.to ?? ctx.nowYear);
  return [...years].sort((a, b) => a - b).filter((year) => year >= from && year <= to);
}
function twoDigitYear(yy, ctx, era) {
  return yearsInWindow([1900 + yy, 2e3 + yy], ctx, era)[0];
}
function monthCandidates(years, month, ctx) {
  return years.filter((year) => year < ctx.nowYear || month <= ctx.nowMonth).map((year) => ({ year, month, display: formatMonthYear(year, month) }));
}
function weekYearCandidates(years, week, ctx) {
  return years.filter((year) => year < ctx.nowYear || !weekInFuture(week, ctx)).map((year) => ({ year, display: `${year}` }));
}
function yearOnlyCandidates(years) {
  return years.map((year) => ({ year, display: `${year}` }));
}
function weekInFuture(week, ctx) {
  return week > Math.ceil(ctx.nowMonth * 31 / 7) + 1;
}
function weekMeaning(week) {
  const dayOfYear = (week - 1) * 7 + 3;
  const monthEnds = [31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 366];
  const month = monthEnds.findIndex((end) => dayOfYear <= end);
  return `Week ${week} of the year (around ${MONTH_NAMES[month === -1 ? 11 : month]})`;
}
function hasBrand(ctx, folded) {
  return ctx.brandNames.includes(folded);
}
function positiveMod(value, modulus) {
  return (value % modulus + modulus) % modulus;
}

// src/brands/alliance.ts
var ALLIANCE_PATTERN = /^(\d{2})(\d{2})(\d{6})$/;
var allianceSerialDecoder = {
  key: "alliance",
  brandNames: ["Speed Queen", "Huebsch", "Alliance Laundry Systems"],
  decode: decodeAllianceSerial
};
function decodeAllianceSerial(serial, ctx) {
  const match = ALLIANCE_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", sequence = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/aosmith.ts
var YYWW_PATTERN = /^([A-Z]?)(\d{2})(\d{2})([A-Z]?\d{5,9})$/;
var MONTH_LETTERS = "ABCDEFGHJKLM";
var LETTER_ERA_PATTERN = /^([A-Z]?)([A-HJ-M])(\d{2})([A-Z0-9-]{4,})$/;
var aosmithSerialDecoder = {
  key: "aosmith",
  brandNames: [
    "A. O. Smith",
    "AO Smith",
    "A.O. Smith",
    "State",
    "Reliance",
    "American Water Heaters",
    "American Water Heater",
    "US Craftmaster",
    "U.S. Craftmaster",
    "Whirlpool",
    "Takagi"
  ],
  decode: decodeAosmithSerial
};
function decodeAosmithSerial(serial, ctx) {
  return decodeYyww(serial, ctx) ?? decodeLetterEra(serial, ctx);
}
function decodeYyww(serial, ctx) {
  const match = YYWW_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, prefix = "", yearChars = "", weekChars = "", tail = ""] = match;
  const week = Number(weekChars);
  if (week < 1 || week > 53) return void 0;
  const era = hasBrand(ctx, "TAKAGI") ? { from: 2010 } : void 0;
  const year = twoDigitYear(Number(yearChars), ctx, era);
  if (year === void 0) return void 0;
  const alternateYear = year <= 2007 ? twoDigitYear(week, ctx, era) : void 0;
  const years = [...new Set([year, alternateYear].filter((y) => y !== void 0))].sort((a, b) => a - b);
  const candidates = yearOnlyCandidates(years);
  return {
    candidates,
    segments: [
      ...prefix ? [
        {
          kind: "plant",
          chars: prefix,
          label: "Prefix",
          meaning: "Factory code \u2014 not date-related"
        }
      ] : [],
      { kind: "year", chars: yearChars, label: "Year", meaning: joinOr(years) },
      { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
      { kind: "sequence", chars: tail, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ],
    ambiguityNote: "Older A. O. Smith-family serials of this shape put the year in the second digit pair, so two years can fit this serial."
  };
}
function decodeLetterEra(serial, ctx) {
  if (!["AOSMITH", "STATE", "RELIANCE"].some((brand) => hasBrand(ctx, brand))) return void 0;
  const match = LETTER_ERA_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, factory = "", monthLetter = "", yearChars = "", tail = ""] = match;
  const month = MONTH_LETTERS.indexOf(monthLetter) + 1;
  const year = twoDigitYear(Number(yearChars), ctx, { to: 2008 });
  if (month === 0 || year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      ...factory ? [
        {
          kind: "plant",
          chars: factory,
          label: "Plant",
          meaning: "Factory code \u2014 not date-related"
        }
      ] : [],
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "sequence", chars: tail, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/bradfordWhite.ts
var YEAR_LETTERS = "ABCDEFGHJKLMNPSTWXYZ";
var YEAR_ANCHOR = 1984;
var MONTH_LETTERS2 = "ABCDEFGHJKLM";
var BW_PATTERN = /^([A-HJ-NPS-TW-Z])([A-HJ-M])(\d{7,8})$/;
var bradfordWhiteSerialDecoder = {
  key: "bradford-white",
  brandNames: ["Bradford White"],
  decode: decodeBradfordWhiteSerial
};
function decodeBradfordWhiteSerial(serial, ctx) {
  const match = BW_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearLetter = "", monthLetter = "", sequence = ""] = match;
  const yearIndex = YEAR_LETTERS.indexOf(yearLetter);
  const month = MONTH_LETTERS2.indexOf(monthLetter) + 1;
  if (yearIndex === -1 || month === 0) return void 0;
  const era = sequence.length === 7 ? { to: 2007 } : { from: 2007 };
  const years = cycleYears(YEAR_ANCHOR + yearIndex, YEAR_LETTERS.length, ctx, era);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearLetter, label: "Year", meaning: joinOr(years) },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ],
    ambiguityNote: "Bradford White reuses year letters every 20 years, so more than one year can fit this serial."
  };
}

// src/brands/bsh.ts
var FD_PREFIXED = /^FD(\d{2})(\d{2})(\d{0,7})$/;
var BARE_DATE = /^(\d{2})(\d{2})$/;
var BARE_WITH_COUNTER = /^(\d{2})(\d{2})(\d{5})$/;
var bshSerialDecoder = {
  key: "bsh",
  brandNames: ["Bosch", "Siemens", "Thermador", "Gaggenau", "Neff"],
  decode: decodeBshSerial
};
function decodeBshSerial(serial, ctx) {
  const match = FD_PREFIXED.exec(serial) ?? BARE_DATE.exec(serial) ?? BARE_WITH_COUNTER.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", counter = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const yy = Number(yearChars);
  const years = yearsInWindow([1920 + yy, 2020 + yy], ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  const prefixed = serial.startsWith("FD");
  const segments = [
    ...prefixed ? [
      {
        kind: "sequence",
        chars: "FD",
        label: "FD",
        meaning: 'Production-date marker ("Fertigungsdatum")'
      }
    ] : [],
    { kind: "year", chars: yearChars, label: "Year", meaning: `${candidates[0]?.year ?? ""}` },
    { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
    ...counter ? [
      {
        kind: "sequence",
        chars: counter,
        label: "Counter",
        meaning: "Unit counter \u2014 not date-related"
      }
    ] : []
  ];
  return { candidates, segments };
}

// src/brands/carrier.ts
var CARRIER_PATTERN = /^(\d{2})(\d{2})([A-Z])(\d{5})$/;
var carrierSerialDecoder = {
  key: "carrier",
  brandNames: ["Carrier", "Bryant", "Payne", "Day & Night", "Day and Night"],
  decode: decodeCarrierSerial
};
function decodeCarrierSerial(serial, ctx) {
  const match = CARRIER_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, weekChars = "", yearChars = "", plantLetter = "", sequence = ""] = match;
  const week = Number(weekChars);
  if (week < 1 || week > 53) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const candidates = weekYearCandidates([year], week, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "plant", chars: plantLetter, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/frigidaire.ts
var FRIGIDAIRE_PATTERN = /^([A-Z0-9])([A-Z])(\d)(\d{2})(\d{5})$/;
var frigidaireSerialDecoder = {
  key: "frigidaire",
  brandNames: ["Frigidaire", "Electrolux"],
  decode: decodeFrigidaireSerial
};
function decodeFrigidaireSerial(serial, ctx) {
  const match = FRIGIDAIRE_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, plant = "", product = "", yearDigit = "", weekChars = "", sequence = ""] = match;
  const week = Number(weekChars);
  if (week < 1 || week > 53) return void 0;
  const digit = Number(yearDigit);
  const decades = yearsInWindow([1990 + digit, 2e3 + digit, 2010 + digit, 2020 + digit], ctx);
  const candidates = weekYearCandidates(decades, week, ctx);
  if (candidates.length === 0) return void 0;
  const years = candidates.map((c) => c.year);
  return {
    candidates,
    segments: [
      {
        kind: "plant",
        chars: `${plant}${product}`,
        label: "Plant",
        meaning: "Factory and product line \u2014 not date-related"
      },
      { kind: "year", chars: yearDigit, label: "Year", meaning: joinOr(years) },
      { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ],
    ambiguityNote: "Frigidaire serials carry only the last digit of the year, so more than one decade can fit this serial."
  };
}

// src/brands/ge.ts
var GE_DATE_LETTERS = "ADFGHLMRSTVZ";
var GE_YEAR_ANCHOR = 2001;
var GE_SERIAL_PATTERN = /^([ADFGHLMRSTVZ])([ADFGHLMRSTVZ])(\d{6})([A-Z]?)$/;
var geSerialDecoder = {
  key: "ge",
  brandNames: [
    "GE",
    "GE Appliances",
    "General Electric",
    "GE Profile",
    "GE Monogram",
    "Monogram",
    "GE Cafe",
    "Caf\xE9",
    "Hotpoint"
  ],
  decode: decodeGeSerial
};
function decodeGeSerial(serial, ctx) {
  const match = GE_SERIAL_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, monthLetter = "", yearLetter = "", digits = "", suffix = ""] = match;
  const month = GE_DATE_LETTERS.indexOf(monthLetter) + 1;
  const years = cycleYears(GE_YEAR_ANCHOR + GE_DATE_LETTERS.indexOf(yearLetter), GE_DATE_LETTERS.length, ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "year", chars: yearLetter, label: "Year", meaning: joinOr(candidates.map((c) => c.year)) },
      {
        kind: "sequence",
        chars: `${digits}${suffix}`,
        label: "Sequence",
        meaning: "Production sequence \u2014 not date-related"
      }
    ],
    ambiguityNote: "GE reuses year letters every 12 years, so more than one year fits this serial."
  };
}

// src/brands/goodman.ts
var GOODMAN_PATTERN = /^(\d{2})(\d{2})(\d{5,6})$/;
var goodmanSerialDecoder = {
  key: "goodman",
  brandNames: ["Goodman", "Amana", "Daikin"],
  decode: decodeGoodmanSerial
};
function decodeGoodmanSerial(serial, ctx) {
  const match = GOODMAN_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", sequence = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const daikinOnly = hasBrand(ctx, "DAIKIN") && !hasBrand(ctx, "GOODMAN") && !hasBrand(ctx, "AMANA");
  const year = twoDigitYear(Number(yearChars), ctx, daikinOnly ? { from: 2012 } : void 0);
  if (year === void 0) return void 0;
  const amanaAmbiguous = hasBrand(ctx, "AMANA") && year <= 2002;
  const candidates = amanaAmbiguous ? yearOnlyCandidates([year]) : monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      {
        kind: "month",
        chars: monthChars,
        label: "Month",
        meaning: amanaAmbiguous ? "Month on HVAC units \u2014 but a week on 2001\u201302 Amana appliances, so only the year is certain" : MONTH_NAMES[month - 1] ?? ""
      },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/icp.ts
var ICP_PATTERN = /^([A-Z])(\d{2})(\d{2})(\d{5})$/;
var icpSerialDecoder = {
  key: "icp",
  brandNames: [
    "Heil",
    "Tempstar",
    "Comfortmaker",
    "Arcoaire",
    "KeepRite",
    "Airquest",
    "Day & Night",
    "Day and Night"
  ],
  decode: decodeIcpSerial
};
function decodeIcpSerial(serial, ctx) {
  const match = ICP_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, plantLetter = "", yearChars = "", weekChars = "", sequence = ""] = match;
  const week = Number(weekChars);
  if (week < 1 || week > 53) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const candidates = weekYearCandidates([year], week, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "plant", chars: plantLetter, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/lennox.ts
var LENNOX_MONTH_LETTERS = "ABCDEFGHJKLM";
var LENNOX_PATTERN = /^(\d{2})(\d{2})([A-HJ-M])(\d{5})$/;
var lennoxSerialDecoder = {
  key: "lennox",
  brandNames: ["Lennox"],
  decode: decodeLennoxSerial
};
function decodeLennoxSerial(serial, ctx) {
  const match = LENNOX_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, plant = "", yearChars = "", monthLetter = "", sequence = ""] = match;
  const month = LENNOX_MONTH_LETTERS.indexOf(monthLetter) + 1;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (month === 0 || year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "plant", chars: plant, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/lg.ts
var LG_PATTERN = /^(\d)(0[1-9]|1[0-2])([A-Z]{2})([A-Z0-9]{7})$/;
var lgSerialDecoder = {
  key: "lg",
  brandNames: ["LG", "LG Electronics"],
  decode: decodeLgSerial
};
function decodeLgSerial(serial, ctx) {
  const match = LG_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearDigit = "", monthChars = "", plant = "", sequence = ""] = match;
  const month = Number(monthChars);
  const digit = Number(yearDigit);
  const years = yearsInWindow([2e3 + digit, 2010 + digit, 2020 + digit], ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearDigit, label: "Year", meaning: joinOr(candidates.map((c) => c.year)) },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "plant", chars: plant, label: "Plant", meaning: "Assembly plant \u2014 not date-related" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ],
    ambiguityNote: "LG serials carry only the last digit of the year, so more than one decade can fit this serial."
  };
}

// src/brands/maytagLegacy.ts
var YEAR_ORDER = "BDFHKMQSUWYZACEGJLNPRTVX";
var YEAR_ANCHOR2 = 1966;
var MONTH_BY_LETTER = {
  A: 1,
  B: 1,
  C: 2,
  D: 2,
  E: 3,
  F: 3,
  G: 4,
  H: 4,
  J: 5,
  K: 5,
  L: 6,
  M: 6,
  N: 7,
  Q: 7,
  P: 8,
  S: 8,
  R: 9,
  U: 9,
  T: 10,
  W: 10,
  V: 11,
  Y: 11,
  X: 12,
  Z: 12
};
var MAYTAG_LEGACY_PATTERN = /^(\d{6,8})([A-HJ-NP-Z])([A-HJ-NP-Z])$/;
var ENDS_2006 = ["AMANA", "ADMIRAL", "MAGICCHEF"];
var maytagLegacySerialDecoder = {
  key: "maytag-legacy",
  brandNames: ["Maytag", "JennAir", "Jenn-Air", "Amana", "Admiral", "Magic Chef"],
  decode: decodeMaytagLegacySerial
};
function decodeMaytagLegacySerial(serial, ctx) {
  const match = MAYTAG_LEGACY_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, sequence = "", yearLetter = "", monthLetter = ""] = match;
  const yearIndex = YEAR_ORDER.indexOf(yearLetter);
  const month = MONTH_BY_LETTER[monthLetter];
  if (yearIndex === -1 || !month) return void 0;
  const eraEnd = ENDS_2006.some((brand) => hasBrand(ctx, brand)) ? 2006 : 2010;
  const years = cycleYears(YEAR_ANCHOR2 + yearIndex, YEAR_ORDER.length, ctx, { to: eraEnd });
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production series \u2014 not date-related" },
      { kind: "year", chars: yearLetter, label: "Year", meaning: joinOr(years) },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" }
    ],
    ambiguityNote: "Maytag reused year letters every 24 years, so more than one year fits this serial."
  };
}

// src/brands/mitsubishi.ts
var MONTH_CODES = "123456789XYZ";
var MITSUBISHI_PATTERN = /^(\d)([1-9XYZ])([A-Z])(\d{5})([A-Z]?)$/;
var mitsubishiSerialDecoder = {
  key: "mitsubishi",
  brandNames: ["Mitsubishi Electric"],
  decode: decodeMitsubishiSerial
};
function decodeMitsubishiSerial(serial, ctx) {
  const match = MITSUBISHI_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearDigit = "", monthCode = "", letter = "", digits = "", suffix = ""] = match;
  const month = MONTH_CODES.indexOf(monthCode) + 1;
  const years = cycleYears(2e3 + Number(yearDigit), 10, ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearDigit, label: "Year", meaning: joinOr(candidates.map((c) => c.year)) },
      { kind: "month", chars: monthCode, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      {
        kind: "sequence",
        chars: `${letter}${digits}${suffix}`,
        label: "Sequence",
        meaning: "Production sequence \u2014 not date-related"
      }
    ],
    ambiguityNote: "Mitsubishi serials carry only the last digit of the year, so more than one decade can fit this serial."
  };
}

// src/brands/navien.ts
var MONTH_CODES2 = "123456789XYZ";
var MODERN_PATTERN = /^\d{4}([A-Z])(\d{2})([1-9XYZ])(\d{2})\d{5}$/;
var DASHED_FULL_PATTERN = /^\d{4,5}-(20\d{2})(\d{2})(\d{2})-\d{3,4}$/;
var DASHED_SPLIT_PATTERN = /^\d{4,5}-(20\d{2})-(\d)(\d{2})-\d{3,4}$/;
var navienSerialDecoder = {
  key: "navien",
  brandNames: ["Navien"],
  decode: decodeNavienSerial
};
function decodeNavienSerial(serial, ctx) {
  return decodeModern(serial, ctx) ?? decodeDashed(serial, ctx);
}
function decodeModern(serial, ctx) {
  const match = MODERN_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, , yearChars = "", monthCode = "", dayChars = ""] = match;
  const day = Number(dayChars);
  if (day < 1 || day > 31) return void 0;
  const month = MONTH_CODES2.indexOf(monthCode) + 1;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      {
        kind: "sequence",
        chars: serial.slice(0, 5),
        label: "Product",
        meaning: "Product code \u2014 not date-related"
      },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "month", chars: monthCode, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      {
        kind: "sequence",
        chars: serial.slice(8),
        label: "Sequence",
        meaning: "Day and production sequence \u2014 not shown"
      }
    ]
  };
}
function decodeDashed(serial, ctx) {
  const match = DASHED_FULL_PATTERN.exec(serial) ?? DASHED_SPLIT_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", dayChars = ""] = match;
  const month = Number(monthChars);
  const day = Number(dayChars);
  if (month < 1 || month > 12 || day < 1 || day > 31) return void 0;
  const years = yearsInWindow([Number(yearChars)], ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${years[0]}` },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" }
    ]
  };
}

// src/brands/noritz.ts
var NORITZ_PATTERN = /^(20\d{2})\.?(\d{2})-?(\d{4,6})$/;
var noritzSerialDecoder = {
  key: "noritz",
  brandNames: ["Noritz"],
  decode: decodeNoritzSerial
};
function decodeNoritzSerial(serial, ctx) {
  const match = NORITZ_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", sequence = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const years = yearsInWindow([Number(yearChars)], ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${years[0]}` },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/rheem.ts
var MMYY_PATTERN = /^([A-Z]{0,4})(\d{2})(\d{2})([A-Z]?\d{5,9}[A-Z]?)$/;
var LETTER_WWYY_PATTERN = /^((?:RHUN)?[A-Z])(\d{2})(\d{2})(\d{5})$/;
var rheemSerialDecoder = {
  key: "rheem",
  brandNames: ["Rheem", "Ruud", "Richmond", "GE"],
  decode: decodeRheemSerial
};
function decodeRheemSerial(serial, ctx) {
  return decodeLetterWwyy(serial, ctx) ?? decodeMmyy(serial, ctx);
}
function decodeLetterWwyy(serial, ctx) {
  const match = LETTER_WWYY_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, prefix = "", firstPair = "", yearChars = "", sequence = ""] = match;
  const first = Number(firstPair);
  if (first < 1 || first > 53) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const firstMeaning = first > 12 ? weekMeaning(first) : "Week \u2014 or the month, on older units of this shape";
  return {
    candidates: yearOnlyCandidates([year]),
    segments: [
      { kind: "plant", chars: prefix, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "week", chars: firstPair, label: first > 12 ? "Week" : "Week or month", meaning: firstMeaning },
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}
function decodeMmyy(serial, ctx) {
  const match = MMYY_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, prefix = "", monthChars = "", yearChars = "", tail = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  const segments = [
    ...prefix ? [{ kind: "plant", chars: prefix, label: "Prefix", meaning: "Model prefix \u2014 not date-related" }] : [],
    { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
    { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
    { kind: "sequence", chars: tail, label: "Sequence", meaning: "Plant code and sequence \u2014 not date-related" }
  ];
  return { candidates, segments };
}

// src/brands/rinnai.ts
var NUMERIC_PATTERN = /^(\d{2})\.?(\d{2})-?(\d{4,6})$/;
var LETTER_PATTERN = /^([A-Z])([A-HJ-M])\.?([A-Z]{2})-?(\d{6})$/;
var MONTH_LETTERS3 = "ABCDEFGHJKLM";
var YEAR_BY_LETTER = {
  A: 2009,
  B: 2010,
  C: 2011,
  D: 2012,
  E: 2013,
  F: 2014,
  G: 2015,
  H: 2016,
  J: 2017,
  K: 2018,
  L: 2019,
  M: 2020,
  N: 2021,
  O: 2022,
  P: 2022,
  R: 2023,
  S: 2024,
  T: 2025,
  W: 2026,
  X: 2027,
  Y: 2028,
  Z: 2029
};
var rinnaiSerialDecoder = {
  key: "rinnai",
  brandNames: ["Rinnai"],
  decode: decodeRinnaiSerial
};
function decodeRinnaiSerial(serial, ctx) {
  return decodeLetterEra2(serial, ctx) ?? decodeNumericEra(serial, ctx);
}
function decodeLetterEra2(serial, ctx) {
  const match = LETTER_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearLetter = "", monthLetter = "", rest = "", sequence = ""] = match;
  const month = MONTH_LETTERS3.indexOf(monthLetter) + 1;
  const mapped = YEAR_BY_LETTER[yearLetter];
  const years = mapped === void 0 ? [] : yearsInWindow([mapped], ctx);
  const candidates = monthCandidates(years, month, ctx);
  if (month === 0 || candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearLetter, label: "Year", meaning: `${years[0]}` },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      {
        kind: "sequence",
        chars: `${rest}${sequence}`,
        label: "Sequence",
        meaning: "Production sequence \u2014 not date-related"
      }
    ]
  };
}
function decodeNumericEra(serial, ctx) {
  const match = NUMERIC_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", monthChars = "", sequence = ""] = match;
  const month = Number(monthChars);
  if (month < 1 || month > 12) return void 0;
  const year = twoDigitYear(Number(yearChars), ctx, { to: 2008 });
  if (year === void 0) return void 0;
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ]
  };
}

// src/brands/samsung.ts
var YEAR_CYCLE_LETTERS = "NRTWXYLPQSZBCDFGHJKM";
var YEAR_CYCLE_ANCHOR = 2e3;
var ERA = { from: 2001, to: 2025 };
var ALTERNATE_2006 = "A";
var MONTH_CODES3 = "123456789ABC";
var SAMSUNG_15 = /^[A-Z0-9]{7}([A-Z])([1-9ABC])[A-Z0-9]{6}$/;
var SAMSUNG_11 = /^[A-Z0-9]{3}([A-Z])([1-9ABC])[A-Z0-9]{6}$/;
var samsungSerialDecoder = {
  key: "samsung",
  brandNames: ["Samsung"],
  decode: decodeSamsungSerial
};
function decodeSamsungSerial(serial, ctx) {
  const match = serial.length === 15 ? SAMSUNG_15.exec(serial) : serial.length === 11 ? SAMSUNG_11.exec(serial) : null;
  if (!match) return void 0;
  const [, yearLetter = "", monthCode = ""] = match;
  const years = yearsForLetter(yearLetter, ctx);
  const month = MONTH_CODES3.indexOf(monthCode) + 1;
  const candidates = monthCandidates(years, month, ctx);
  if (candidates.length === 0) return void 0;
  const yearPosition = serial.length === 15 ? 7 : 3;
  return {
    candidates,
    segments: buildSegments(
      serial,
      yearPosition,
      candidates.map((c) => c.year),
      month
    ),
    ambiguityNote: "Samsung reuses year letters every 20 years, so more than one year fits this serial."
  };
}
function yearsForLetter(letter, ctx) {
  if (letter === ALTERNATE_2006) return yearsInWindow([2006], ctx, ERA);
  const index = YEAR_CYCLE_LETTERS.indexOf(letter);
  if (index === -1) return [];
  const base = YEAR_CYCLE_ANCHOR + index;
  return yearsInWindow([base, base + 20], ctx, ERA);
}
function buildSegments(serial, yearPosition, years, month) {
  return [
    {
      kind: "sequence",
      chars: serial.slice(0, yearPosition),
      label: "Model info",
      meaning: "Product and factory codes \u2014 not date-related"
    },
    { kind: "year", chars: serial[yearPosition] ?? "", label: "Year", meaning: joinOr(years) },
    { kind: "month", chars: serial[yearPosition + 1] ?? "", label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
    {
      kind: "sequence",
      chars: serial.slice(yearPosition + 2),
      label: "Sequence",
      meaning: "Production sequence \u2014 not date-related"
    }
  ];
}

// src/brands/stiebel.ts
var STIEBEL_PATTERN = /^\d{6}-(\d{2})(\d{2})-\d{7}$/;
var stiebelSerialDecoder = {
  key: "stiebel",
  brandNames: ["Stiebel Eltron", "Tempra"],
  decode: decodeStiebelSerial
};
function decodeStiebelSerial(serial, ctx) {
  const match = STIEBEL_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", weekChars = ""] = match;
  const week = Number(weekChars) - 25;
  if (week < 1 || week > 53) return void 0;
  const year = twoDigitYear((Number(yearChars) + 25) % 100, ctx);
  if (year === void 0) return void 0;
  const candidates = weekYearCandidates([year], week, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) }
    ],
    // finalizeSegments owns the year meaning, so the offset quirk goes in a note.
    notes: ["Stiebel Eltron offsets both date fields by 25 on the plate \u2014 the values shown are already decoded."]
  };
}

// src/brands/trane.ts
var TRANE_2010_PATTERN = /^(\d{2})(\d{2})([0-9A-Z]{6})$/;
var TRANE_2002_PATTERN = /^([2-9])(\d{2})([0-9A-Z]{6})$/;
var traneSerialDecoder = {
  key: "trane",
  brandNames: ["Trane", "American Standard", "American Standard Heating & Air Conditioning"],
  decode: decodeTraneSerial
};
function decodeTraneSerial(serial, ctx) {
  if (serial.length === 10) {
    const match = TRANE_2010_PATTERN.exec(serial);
    if (!match) return void 0;
    const [, yearChars = "", weekChars = "", sequence = ""] = match;
    const year = twoDigitYear(Number(yearChars), ctx, { from: 2010 });
    return buildDecode(yearChars, year, weekChars, sequence, ctx);
  }
  if (serial.length === 9) {
    const match = TRANE_2002_PATTERN.exec(serial);
    if (!match) return void 0;
    const [, yearChar = "", weekChars = "", sequence = ""] = match;
    return buildDecode(yearChar, 2e3 + Number(yearChar), weekChars, sequence, ctx);
  }
  return void 0;
}
function buildDecode(yearChars, year, weekChars, sequence, ctx) {
  const week = Number(weekChars);
  if (year === void 0 || week < 1 || week > 53) return void 0;
  const candidates = weekYearCandidates([year], week, ctx);
  if (candidates.length === 0) return void 0;
  const segments = [
    { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
    { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
    { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
  ];
  return { candidates, segments };
}

// src/brands/uline.ts
var ULINE_PATTERN = /^(\d{2})(\d{5})(0[1-9]|1[0-2])(\d{4})$/;
var FACTORY_MEANING = "Factory use only \u2014 not date-related";
var ulineSerialDecoder = {
  key: "uline",
  brandNames: ["U-Line", "U-Line Refrigeration"],
  decode: decodeUlineSerial
};
function decodeUlineSerial(serial, ctx) {
  const match = ULINE_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, yearChars = "", leadingFactory = "", monthChars = "", trailingFactory = ""] = match;
  const year = twoDigitYear(Number(yearChars), ctx);
  if (year === void 0) return void 0;
  const month = Number(monthChars);
  const candidates = monthCandidates([year], month, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "year", chars: yearChars, label: "Year", meaning: `${year}` },
      { kind: "sequence", chars: leadingFactory, label: "Factory use", meaning: FACTORY_MEANING },
      { kind: "month", chars: monthChars, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: trailingFactory, label: "Factory use", meaning: FACTORY_MEANING }
    ]
  };
}

// src/brands/weber.ts
var AUTHORIZED = "AUTHORIZED";
var CONSUMABLE = "CONSUMABLE";
var BARE_CODE = /^([A-Z]{2})$/;
var SERIAL_CODE = /^([A-Z]{2})(\d{7})$/;
var MODEL_YEAR_NOTE = "Weber codes give the model year (which runs October\u2013September), not the exact build date.";
var weberSerialDecoder = {
  key: "weber",
  brandNames: ["Weber"],
  decode: decodeWeberSerial
};
function decodeWeberSerial(serial, ctx) {
  const bare = BARE_CODE.exec(serial);
  if (bare) return decodePrefix(bare[1] ?? "", "", [AUTHORIZED], { from: 1994, to: 2014 }, ctx);
  const etched = SERIAL_CODE.exec(serial);
  if (etched) return decodePrefix(etched[1] ?? "", etched[2] ?? "", [CONSUMABLE, AUTHORIZED], { from: 2012 }, ctx);
  return void 0;
}
function decodePrefix(prefix, sequence, keywords, era, ctx) {
  const years = /* @__PURE__ */ new Set();
  for (const keyword of keywords) {
    const year2 = cipherYear(prefix, keyword, era, ctx);
    if (year2 !== void 0) years.add(year2);
  }
  if (years.size !== 1) return void 0;
  const year = [...years][0];
  return {
    candidates: yearOnlyCandidates([year]),
    segments: [
      { kind: "year", chars: prefix, label: "Year code", meaning: `${year}` },
      ...sequence ? [
        {
          kind: "sequence",
          chars: sequence,
          label: "Sequence",
          meaning: "Production sequence \u2014 not date-related"
        }
      ] : []
    ],
    notes: [MODEL_YEAR_NOTE]
  };
}
function cipherYear(prefix, keyword, era, ctx) {
  if ([...prefix].some((letter) => !keyword.includes(letter))) return void 0;
  const digits = [...prefix].map((letter) => (keyword.indexOf(letter) + 1) % 10);
  const yy = Number(digits.join(""));
  return yearsInWindow([1900 + yy, 2e3 + yy], ctx, era)[0];
}

// src/brands/whirlpool.ts
var YEAR_LETTERS2 = "XABCDEFGHJKLMPRSTUWY";
var YEAR_LETTER_ANCHOR = 1990;
var YEAR_CYCLE = 30;
var ONE_LETTER_DIVISION = /^([A-Z])([A-Z0-9])(\d{2})(\d{5,7})$/;
var TWO_LETTER_DIVISION = /^([A-Z]{2})([A-Z0-9])(\d{2})(\d{5,7})$/;
var whirlpoolSerialDecoder = {
  key: "whirlpool",
  brandNames: [
    "Whirlpool",
    "KitchenAid",
    "Maytag",
    "JennAir",
    "Jenn-Air",
    "Amana",
    "Admiral",
    "Roper",
    "Estate",
    "Inglis"
  ],
  decode: decodeWhirlpoolSerial
};
function decodeWhirlpoolSerial(serial, ctx) {
  const parses = [ONE_LETTER_DIVISION, TWO_LETTER_DIVISION].map((pattern) => tryParse(pattern.exec(serial), ctx)).filter((parse) => !!parse);
  return parses.length === 1 ? parses[0] : void 0;
}
function tryParse(match, ctx) {
  if (!match) return void 0;
  const [, division = "", yearChar = "", weekChars = "", sequence = ""] = match;
  const week = Number(weekChars);
  if (week < 1 || week > 53) return void 0;
  const candidates = weekYearCandidates(yearsForChar(yearChar, ctx), week, ctx);
  if (candidates.length === 0) return void 0;
  return {
    candidates,
    segments: buildSegments2(
      division,
      yearChar,
      weekChars,
      week,
      sequence,
      candidates.map((c) => c.year)
    ),
    // Unreachable while MAX_PLAUSIBLE_AGE_YEARS (25) < the 30-year cycle — kept as a guard so a
    // future wider window degrades honestly instead of silently single-picking.
    ambiguityNote: "Whirlpool reuses year codes every 30 years, so more than one year fits this serial."
  };
}
function yearsForChar(yearChar, ctx) {
  if (/\d/.test(yearChar)) {
    const digit = Number(yearChar);
    return yearsInWindow([1980 + digit, 2010 + digit], ctx);
  }
  const index = YEAR_LETTERS2.indexOf(yearChar);
  if (index === -1) return [];
  const base = YEAR_LETTER_ANCHOR + index;
  return yearsInWindow([base, base + YEAR_CYCLE], ctx);
}
function buildSegments2(division, yearChar, weekChars, week, sequence, years) {
  return [
    { kind: "plant", chars: division, label: "Plant", meaning: "Manufacturing division \u2014 not date-related" },
    { kind: "year", chars: yearChar, label: "Year", meaning: joinOr(years) },
    { kind: "week", chars: weekChars, label: "Week", meaning: weekMeaning(week) },
    { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
  ];
}

// src/brands/york.ts
var YORK_MONTH_LETTERS = "ABCDEFGHKLMN";
var MODERN_PATTERN2 = /^([A-Z])(\d)([A-HK-N])(\d)(\d{6})$/;
var LEGACY_PATTERN = /^(?:\([A-Z]\))?([A-Z])([A-HK-N])([A-HJ-NP-Y])([MPSTYZ])(\d{6})$/;
var LEGACY_YEARS = {
  A: [1971, 1992],
  B: [1972, 1993],
  C: [1973, 1994],
  D: [1974, 1995],
  E: [1975, 1996],
  F: [1976, 1997],
  G: [1977, 1998],
  H: [1978, 1999],
  J: [1979, 2e3],
  K: [1980, 2001],
  L: [1981, 2002],
  M: [1982, 2003],
  N: [1983, 2004],
  P: [1984],
  R: [1985],
  S: [1986],
  T: [1987],
  V: [1988],
  W: [1989],
  X: [1990],
  Y: [1991]
};
var yorkSerialDecoder = {
  key: "york",
  brandNames: ["York", "Coleman", "Luxaire"],
  decode: decodeYorkSerial
};
function decodeYorkSerial(serial, ctx) {
  return decodeModern2(serial, ctx) ?? decodeLegacy(serial, ctx);
}
function decodeModern2(serial, ctx) {
  const match = MODERN_PATTERN2.exec(serial);
  if (!match) return void 0;
  const [, plant = "", yearTens = "", monthLetter = "", yearUnits = "", sequence = ""] = match;
  const month = YORK_MONTH_LETTERS.indexOf(monthLetter) + 1;
  const years = yearsInWindow([2e3 + Number(`${yearTens}${yearUnits}`)], ctx, { from: 2004 });
  const candidates = monthCandidates(years, month, ctx);
  if (month === 0 || candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "plant", chars: plant, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "year", chars: `${yearTens}\u2026${yearUnits}`, label: "Year", meaning: `${years[0]}` },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "sequence", chars: sequence, label: "Sequence", meaning: "Production sequence \u2014 not date-related" }
    ],
    // finalizeSegments owns the year meaning, so the split-position quirk goes in a note.
    notes: ["The two year digits sit at positions 2 and 4, on either side of the month letter."]
  };
}
function decodeLegacy(serial, ctx) {
  const match = LEGACY_PATTERN.exec(serial);
  if (!match) return void 0;
  const [, plant = "", monthLetter = "", yearLetter = "", typeLetter = "", sequence = ""] = match;
  const month = YORK_MONTH_LETTERS.indexOf(monthLetter) + 1;
  const years = yearsInWindow(LEGACY_YEARS[yearLetter] ?? [], ctx, { to: 2004 });
  const candidates = monthCandidates(years, month, ctx);
  if (month === 0 || candidates.length === 0) return void 0;
  return {
    candidates,
    segments: [
      { kind: "plant", chars: plant, label: "Plant", meaning: "Factory code \u2014 not date-related" },
      { kind: "month", chars: monthLetter, label: "Month", meaning: MONTH_NAMES[month - 1] ?? "" },
      { kind: "year", chars: yearLetter, label: "Year", meaning: joinOr(years) },
      {
        kind: "sequence",
        chars: `${typeLetter}${sequence}`,
        label: "Sequence",
        meaning: "Item type and sequence \u2014 not date-related"
      }
    ],
    ambiguityNote: "York reused year letters every 21 years, so more than one year can fit this serial."
  };
}

// src/decodeSerial.ts
var MAX_PLAUSIBLE_AGE_YEARS = 25;
var DECODERS = [
  geSerialDecoder,
  whirlpoolSerialDecoder,
  maytagLegacySerialDecoder,
  frigidaireSerialDecoder,
  lgSerialDecoder,
  samsungSerialDecoder,
  bshSerialDecoder,
  allianceSerialDecoder,
  weberSerialDecoder,
  ulineSerialDecoder,
  carrierSerialDecoder,
  icpSerialDecoder,
  traneSerialDecoder,
  goodmanSerialDecoder,
  lennoxSerialDecoder,
  yorkSerialDecoder,
  mitsubishiSerialDecoder,
  // Water heaters. `rheem` claims GE (Rheem-made tanks), so it must stay after `ge`;
  // `aosmith` claims Whirlpool (American-made water heaters), so it stays after `whirlpool`.
  rheemSerialDecoder,
  aosmithSerialDecoder,
  bradfordWhiteSerialDecoder,
  navienSerialDecoder,
  rinnaiSerialDecoder,
  noritzSerialDecoder,
  stiebelSerialDecoder
];
function decodeSerial(input) {
  const foldedNames = input.brandNames.map(foldBrandName);
  const serial = normalizeSerial(input.serial);
  const nowYear = Number(input.now.slice(0, 4));
  const nowMonth = Number(input.now.slice(5, 7));
  const ctx = {
    minYear: nowYear - MAX_PLAUSIBLE_AGE_YEARS,
    nowYear,
    nowMonth,
    hints: input.hints,
    brandNames: foldedNames
  };
  const match = firstDecodeMatch(serial, foldedNames, ctx);
  if (!match) return void 0;
  const { decoder, raw } = match;
  const confirmed = input.confirmation && matchConfirmation(serial, raw.candidates, input.confirmation);
  if (confirmed) {
    return buildDecode2(decoder.key, serial, raw, [confirmed], [], {
      best: confirmed,
      provenance: "user-confirmed",
      describe: raw.candidates
    });
  }
  const { candidates, hintNotes } = applyHints(raw.candidates, input.hints);
  return buildDecode2(decoder.key, serial, raw, candidates, hintNotes, {
    ...candidates.length === 1 ? { best: candidates[0], provenance: "inferred" } : { provenance: "ambiguous" },
    staleConfirmation: !!input.confirmation
  });
}
function normalizeSerial(serial) {
  return foldNameplateGlyphs(serial).toUpperCase().replace(/\s+/g, "");
}
function foldBrandName(name) {
  return normalizeNameKey(name);
}
var CLAIMED_NAMES = new Map(DECODERS.map((d) => [d, new Set(d.brandNames.map(foldBrandName))]));
function firstDecodeMatch(serial, foldedNames, ctx) {
  for (const decoder of DECODERS) {
    if (!decoderClaimsBrand(decoder, foldedNames)) continue;
    const raw = decoder.decode(serial, ctx);
    if (raw && raw.candidates.length > 0) return { decoder, raw };
  }
  return void 0;
}
function decoderClaimsBrand(decoder, foldedNames) {
  const claimed = CLAIMED_NAMES.get(decoder);
  return foldedNames.some((name) => !!claimed?.has(name));
}
function matchConfirmation(serial, candidates, confirmation) {
  if (normalizeSerial(confirmation.serial) !== serial) return void 0;
  return candidates.find((c) => c.year === confirmation.year);
}
function applyHints(candidates, hints) {
  let narrowed = candidates;
  const hintNotes = [];
  const observedAt = hints?.observedAt;
  if (observedAt) {
    const obsYear = Number(observedAt.slice(0, 4));
    const obsMonth = Number(observedAt.slice(5, 7));
    const filtered = narrowed.filter(
      (c) => c.year < obsYear || c.year === obsYear && (c.month === void 0 || c.month <= obsMonth)
    );
    if (filtered.length > 0) narrowed = filtered;
  }
  const era = hints?.modelEra;
  if (era && narrowed.length > 1) {
    const filtered = narrowed.filter(
      (c) => (era.from === void 0 || c.year >= era.from) && (era.to === void 0 || c.year <= era.to)
    );
    if (filtered.length > 0 && filtered.length < narrowed.length) {
      narrowed = filtered;
      hintNotes.push("Narrowed using the model's known production years.");
    }
  }
  return { candidates: narrowed, hintNotes };
}
function buildDecode2(brandKey, serial, raw, candidates, hintNotes, resolution) {
  const described = resolution.describe ?? candidates;
  const ambiguityNotes = described.length > 1 && raw.ambiguityNote ? [raw.ambiguityNote] : [];
  return {
    brandKey,
    serial,
    candidates,
    yearOptions: [...new Set(raw.candidates.map((c) => c.year))],
    best: resolution.best,
    provenance: resolution.provenance,
    summary: buildSummary(candidates, resolution.best),
    segments: finalizeSegments(raw.segments, described),
    notes: [...raw.notes ?? [], ...ambiguityNotes, ...hintNotes],
    ...resolution.staleConfirmation ? { staleConfirmation: true } : {}
  };
}
function finalizeSegments(segments, candidates) {
  const years = joinOr([...new Set(candidates.map((c) => c.year))]);
  return segments.map((s) => s.kind === "year" ? { ...s, meaning: years } : s);
}
function buildSummary(candidates, best) {
  if (best) return best.display;
  const month = candidates[0]?.month;
  if (month !== void 0 && candidates.every((c) => c.month === month)) {
    return `${MONTH_ABBREVIATIONS[month - 1]} ${joinOr(candidates.map((c) => c.year))}`;
  }
  return joinOr(candidates.map((c) => c.display));
}

// src/decodeEquipmentSerial.ts
function decodeEquipmentSerial(equipment, brand, now) {
  const serial = equipment.fields?.serialNumber;
  if (!serial) return void 0;
  const rawName = unitBrandName(equipment);
  const brandNames = [brand?.canonicalName, brand?.displayName, ...brand?.aliases ?? [], rawName].filter(
    (name) => !!name
  );
  if (brandNames.length === 0) return void 0;
  return decodeSerial({
    brandNames,
    serial,
    hints: {
      modelNumber: equipment.details?.modelDisplay ?? equipment.fields?.modelNumber,
      observedAt: equipment.createdAt ? toLocalDate(equipment.createdAt) : void 0
    },
    confirmation: equipment.serialConfirmation,
    now
  });
}
export {
  MAX_PLAUSIBLE_AGE_YEARS,
  MONTH_ABBREVIATIONS,
  MONTH_NAMES,
  decodeEquipmentSerial,
  decodeSerial,
  foldBrandName,
  formatMonthYear,
  joinOr,
  normalizeSerial
};
//# sourceMappingURL=index.js.map